import * as THREE from 'three';
import { basic, cloth, emis, leather, metal, std } from '../materials.ts';
import { G, M, Ribbon, V, glowSprite, limb, wisp } from '../prims.ts';
import { kit } from './era4-kit.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/*
 * Vela, the Archer without arrows (docs/ANTIHEROES.md): the old garrison's scout, who ran out of arrows on the wall
 * walk and kept watching. The titan dreams her slender, in a scout's light plate turned to black glass cracked with
 * cold blue light; a deep hood over a glass mask with two blue eyes; a tall bow of black glass in her left hand,
 * its string a thread of pale light with no arrow on it; on her back an empty quiver of broken shafts; a long
 * ragged mantle of smoke from her shoulders.
 *
 * Bones as Ser Corvin's (so she walks, reels and falls on the sentinel's clips): body, chest, head, flapF, flapB,
 * armL/foreL/shield (here, the bow hand), armR/foreR/sword (here, the drawing hand), legs. Faces +Z, `R` on +X.
 */

/** Model height (the hood's peak). */
export const VELA_HEIGHT = 2.15;

export const vela = (o?: BaseOpts): Model => {
  const c = begin(o, 0.6, 811);
  const { g, rng } = c;
  const k4 = kit(rng);

  const glass = metal('#2a2f3c', { roughness: 0.16, metalness: 0.5, bumpScale: 0.01 });
  const glassD = metal('#161922', { roughness: 0.28, metalness: 0.5, bumpScale: 0.02 });
  const hide = leather('#191c26', { bumpScale: 0.05 });
  const hood = cloth('#141724', { side: THREE.DoubleSide });
  const rag = cloth('#1a1e2c', { side: THREE.DoubleSide });
  const wood = std('#151417', { roughness: 0.8, bumpScale: 0.05 });
  const cold = emis('#e4eeff', '#5f8cff', 2.8);
  const coldF = emis('#cfe0ff', '#7fa8ff', 2.2, { transparent: true, opacity: 0.85, depthWrite: false });
  const smokeM = basic('#1a1e2c', 0.55);

  const bone = (parent: THREE.Object3D, at: THREE.Vector3, name: string): { p: THREE.Group; abs: THREE.Group } => joint(parent, at, name);

  /* ---------- legs: long glass greaves over dark leather, cold light in the knees ---------- */
  const legs: Record<string, THREE.Object3D> = {};
  for (const k of [-1, 1]) {
    const s = k < 0 ? 'L' : 'R';
    const legB = bone(g, V(k * 0.13, 0.98, 0), `leg${s}`);
    const hip = legB.abs;
    limb(hip, V(k * 0.13, 1.0, 0), V(k * 0.14, 0.56, 0.02), 0.11, 0.085, hide, 7);
    k4.crack(hip, [V(k * 0.17, 0.94, 0.08), V(k * 0.18, 0.8, 0.1), V(k * 0.16, 0.66, 0.09)], 0.006, cold, 0.025);
    const shinB = bone(hip, V(k * 0.14, 0.55, 0.02), `shin${s}`);
    const lo = shinB.abs;
    const cop = k4.swell(lo, glassD, k * 0.14, 0.55, 0.07, 0.07, 0.075, 0.065, 7, 4);
    cop.rotation.x = -0.3;
    M(new THREE.SphereGeometry(0.018, 5, 4), cold, lo).position.set(k * 0.14, 0.55, 0.13);
    limb(lo, V(k * 0.14, 0.53, 0.02), V(k * 0.14, 0.12, 0.0), 0.088, 0.065, glass, 7);
    k4.crack(lo, [V(k * 0.14, 0.47, 0.085), V(k * 0.16, 0.34, 0.08), V(k * 0.13, 0.2, 0.072)], 0.006, cold, 0.025);
    const boot = M(new THREE.BoxGeometry(0.14, 0.09, 0.24), glassD, lo);
    boot.position.set(k * 0.14, 0.045, 0.04);
    const toe = M(new THREE.ConeGeometry(0.06, 0.12, 4), glassD, lo);
    toe.position.set(k * 0.14, 0.04, 0.19);
    toe.rotation.set(Math.PI / 2, Math.PI / 4, 0);
    toe.scale.set(1, 1, 0.5);
    legs[`leg${s}`] = legB.p;
    legs[`shin${s}`] = shinB.p;
  }

  /* ---------- hips: a leather skirt split for running, a belt, ragged flaps ---------- */
  const bodyB = bone(g, V(0, 1.0, 0), 'body');
  const body = bodyB.abs;
  M(new THREE.CylinderGeometry(0.22, 0.28, 0.28, 9, 1, true), hide, body).position.set(0, 0.86, 0);
  M(new THREE.CylinderGeometry(0.21, 0.24, 0.16, 9), glassD, body).position.set(0, 1.02, 0);
  const belt = M(new THREE.TorusGeometry(0.22, 0.02, 3, 12), glassD, body);
  belt.rotation.x = Math.PI / 2;
  belt.position.set(0, 1.08, 0);
  const flapF = bone(body, V(0, 1.06, 0.18), 'flapF');
  k4.panel(flapF.abs, V(0, 1.07, 0.2), 0.24, 0.62, 0.02, 1, rag, 0.55);
  const flapB = bone(body, V(0, 1.06, -0.18), 'flapB');
  k4.panel(flapB.abs, V(0, 1.07, -0.2), 0.3, 0.7, 0.02, -1, rag, 0.5);

  /* ---------- chest: a slim glass cuirass with a cold crack, small pauldrons, the empty quiver ---------- */
  const chestB = bone(body, V(0, 1.12, 0), 'chest');
  const chest = chestB.abs;
  k4.swell(chest, glass, 0, 1.38, 0.0, 0.25, 0.28, 0.18, 9, 6);
  k4.swell(chest, glassD, 0, 1.2, 0.01, 0.22, 0.12, 0.16, 8, 4);
  k4.crack(chest, [V(0.15, 1.58, 0.13), V(0.07, 1.48, 0.175), V(0.0, 1.38, 0.185), V(-0.07, 1.28, 0.175), V(-0.04, 1.17, 0.16)], 0.011, cold, 0.05);
  k4.crack(chest, [V(0.0, 1.38, 0.185), V(0.1, 1.31, 0.17), V(0.15, 1.22, 0.14)], 0.007, cold, 0.03);
  glowSprite(chest, '#7fa8ff', 0.36, V(0.0, 1.38, 0.23), 0.5);
  M(new THREE.CylinderGeometry(0.1, 0.13, 0.08, 8), glassD, chest).position.set(0, 1.6, 0);
  for (const k of [-1, 1]) {
    const pd = G(chest, k * 0.29, 1.59, 0);
    pd.rotation.z = -k * 0.4;
    const dome = M(new THREE.SphereGeometry(1, 8, 4, 0, Math.PI * 2, 0, Math.PI / 2), glass, pd);
    dome.scale.set(0.15, 0.11, 0.15);
    const lame = M(new THREE.CylinderGeometry(0.145, 0.15, 0.05, 8, 1, true), glassD, pd);
    lame.position.y = -0.05;
  }
  // A bracer of glass on the bow arm side lives on the forearm; here the baldric across the chest.
  const bald = M(new THREE.BoxGeometry(0.05, 0.62, 0.02), hide, chest);
  bald.position.set(0.0, 1.38, 0.19);
  bald.rotation.z = 0.75;
  // The quiver on her back: dark wood, glass bands, only broken shafts in it, a cold glint at the mouth.
  {
    const q = G(chest, 0.1, 1.38, -0.22);
    q.rotation.set(-0.15, 0, -0.45);
    M(new THREE.CylinderGeometry(0.075, 0.06, 0.6, 8), wood, q);
    for (const y of [-0.22, 0.2]) {
      const band = M(new THREE.TorusGeometry(0.074, 0.012, 3, 10), glassD, q);
      band.rotation.x = Math.PI / 2;
      band.position.y = y;
    }
    for (const [x, z, h, r] of [[0.03, 0.02, 0.18, 0.2], [-0.03, -0.01, 0.12, -0.25], [0.0, -0.03, 0.22, 0.05]] as const) {
      const sh = M(new THREE.CylinderGeometry(0.008, 0.008, h, 4), wood, q);
      sh.position.set(x, 0.3 + h / 2, z);
      sh.rotation.z = r;
    }
    glowSprite(q, '#5f8cff', 0.18, V(0, 0.33, 0), 0.6);
  }

  /* ---------- the mantle of smoke: long ragged panels down her back ---------- */
  const mantle = G(chest, 0, 1.6, -0.17);
  for (const [x, w, h, rz] of [[-0.15, 0.26, 1.3, 0.07], [0.15, 0.26, 1.25, -0.07], [0, 0.3, 1.4, 0]] as const) {
    const pn = k4.panel(mantle, V(x, 0, -0.02), w, h, 0.02, -1, smokeM, 0.65);
    pn.rotation.set(0.14, 0, rz);
  }

  /* ---------- head: a deep hood to a peak, a glass mask with two blue eyes ---------- */
  const headB = bone(chest, V(0, 1.64, 0.01), 'head');
  const head = headB.abs;
  M(new THREE.SphereGeometry(0.11, 9, 7), glass, head).position.set(0, 1.77, 0.02);
  const mask = M(new THREE.SphereGeometry(0.1, 9, 6, -Math.PI / 2, Math.PI, 0, Math.PI * 0.75), glassD, head);
  mask.position.set(0, 1.77, 0.04);
  for (const k of [-1, 1]) {
    const eye = M(new THREE.BoxGeometry(0.05, 0.014, 0.02), cold, head);
    eye.position.set(k * 0.04, 1.79, 0.135);
    eye.rotation.z = k * 0.18;
    glowSprite(head, '#7fa8ff', 0.22, V(k * 0.04, 1.79, 0.16), 0.95);
  }
  k4.crack(head, [V(-0.07, 1.86, 0.1), V(-0.05, 1.8, 0.125), V(-0.06, 1.72, 0.12)], 0.006, cold, 0.02);
  // The hood: an open cone over the head, its peak bent back, its rim hanging over the brow.
  const hd = G(head, 0, 1.8, -0.01);
  // A deep cowl: a sphere open at the face, drawn up into a short peak bent back.
  const cowl = M(new THREE.SphereGeometry(0.165, 12, 8, Math.PI * 0.72, Math.PI * 1.56, 0, Math.PI * 0.72), hood, hd);
  cowl.position.set(0, -0.02, -0.01);
  cowl.scale.set(1, 1.15, 1.12);
  const peak = M(new THREE.ConeGeometry(0.09, 0.2, 8, 1, true), hood, hd);
  peak.position.set(0, 0.2, -0.07);
  peak.rotation.x = -0.7;
  const rim = M(new THREE.TorusGeometry(0.15, 0.025, 4, 12, Math.PI * 1.1), hood, hd);
  rim.position.set(0, -0.03, 0.06);
  rim.rotation.set(-0.3, 0, Math.PI * -0.05);
  k4.panel(hd, V(0, -0.05, -0.12), 0.32, 0.36, 0.02, -1, hood, 0.4);

  /* ---------- the bow arm: a tall bow of black glass, cracked with light; its string a thread of light ---------- */
  const armLB = bone(chest, V(-0.29, 1.54, 0), 'armL');
  const aL = armLB.abs;
  limb(aL, V(-0.3, 1.54, 0), V(-0.34, 1.22, 0.05), 0.07, 0.06, glass, 6);
  const foreLB = bone(aL, V(-0.34, 1.21, 0.06), 'foreL');
  const fL = foreLB.abs;
  limb(fL, V(-0.34, 1.21, 0.06), V(-0.3, 1.0, 0.24), 0.06, 0.05, glassD, 6);
  const shieldB = bone(fL, V(-0.3, 0.98, 0.27), 'shield');
  const sh = shieldB.abs;
  M(new THREE.BoxGeometry(0.07, 0.08, 0.08), glassD, sh).position.set(-0.3, 0.98, 0.27);
  {
    const bow = G(sh, -0.3, 1.12, 0.28);
    bow.rotation.set(0.12, 0.25, 0);
    // Two curved limbs (recurved at the tips), the grip between, cracks of light along them.
    const pts = (k: number): THREE.Vector3[] => [V(0, 0.08 * k, 0), V(0, 0.3 * k, -0.07), V(0, 0.52 * k, -0.08), V(0, 0.7 * k, -0.02), V(0, 0.78 * k, 0.05)];
    for (const k of [-1, 1]) {
      k4.tube(bow, pts(k), 0.024, glass, 6);
      k4.crack(bow, pts(k).slice(0, 4).map((p) => p.clone().add(V(0, 0, -0.02))), 0.006, cold, 0.01);
      M(new THREE.SphereGeometry(0.02, 5, 4), cold, bow).position.set(0, 0.78 * k, 0.05);
    }
    M(new THREE.CylinderGeometry(0.03, 0.03, 0.18, 6), wood, bow);
    // The string: a thread of pale light, tip to tip, nothing nocked on it.
    const str = M(new THREE.CylinderGeometry(0.005, 0.005, 1.54, 4), coldF, bow);
    str.position.set(0, 0, 0.06);
    glowSprite(bow, '#cfe0ff', 0.4, V(0, 0, 0.06), 0.35);
    glowSprite(bow, '#7fa8ff', 0.25, V(0, 0.76, 0.05), 0.7);
    glowSprite(bow, '#7fa8ff', 0.25, V(0, -0.76, 0.05), 0.7);
  }

  /* ---------- the drawing arm: a glass gauntlet, smoke curling off the fingers ---------- */
  const armRB = bone(chest, V(0.29, 1.54, 0), 'armR');
  const aR = armRB.abs;
  limb(aR, V(0.3, 1.54, 0), V(0.35, 1.21, 0.02), 0.07, 0.06, glass, 6);
  const foreRB = bone(aR, V(0.35, 1.2, 0.02), 'foreR');
  const fR = foreRB.abs;
  k4.swell(fR, glassD, 0.35, 1.2, 0.02, 0.06, 0.06, 0.06, 6, 4);
  limb(fR, V(0.35, 1.2, 0.02), V(0.37, 0.95, 0.08), 0.058, 0.05, glass, 6);
  const swordB = bone(fR, V(0.37, 0.92, 0.09), 'sword');
  const sw = swordB.abs;
  M(new THREE.BoxGeometry(0.075, 0.085, 0.085), glassD, sw).position.set(0.37, 0.92, 0.09);
  M(new THREE.SphereGeometry(0.02, 5, 4), cold, sw).position.set(0.37, 0.87, 0.13);
  glowSprite(sw, '#7fa8ff', 0.22, V(0.37, 0.87, 0.14), 0.6);

  /* ---------- smoke breathing off her ---------- */
  const wisps: Ribbon[] = [];
  for (const [x, z, h, y] of [[0.26, -0.18, 0.6, 1.5], [-0.24, -0.2, 0.55, 1.45], [0.04, -0.28, 0.8, 1.25], [0.37, 0.09, 0.4, 0.85]] as const) {
    wisps.push(new Ribbon(chest, wisp(x, z, h, 4).map((p) => p.add(V(0, y, 0))), 0.045, smokeM, rng, 0.06));
  }

  const t = (_n: number): void => {
    for (const r of wisps) r.step();
  };
  t(0);
  return done(c, VELA_HEIGHT, t, undefined, bonesOf(c, {
    body: bodyB.p, chest: chestB.p, head: headB.p, flapF: flapF.p, flapB: flapB.p,
    armL: armLB.p, foreL: foreLB.p, shield: shieldB.p, armR: armRB.p, foreR: foreRB.p, sword: swordB.p,
    legL: legs.legL!, shinL: legs.shinL!, legR: legs.legR!, shinR: legs.shinR!,
  }));
};
